import React, { useEffect, useState } from "react";
import { apiConstants, getGeneric } from "../../common/constants";
import { Box, Button, Card, CardContent } from "@mui/material";
import { formatDate, formatNumber } from "../../helpers/UtilityHelper";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import MUIModal from "../../components/MuiModal";
import Loader from "../../common/Loader";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import { downloadFile } from "../../common/util";
import { useForm } from "react-hook-form";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import _ from "lodash";
import { Summarize } from "@mui/icons-material";
import OrderRowActions from "../../common/OrderRowActions";
import { FieldLabel, FilterField } from "../../common/FilterBar";
import CopyIcon from "../../common/CopyIcon";

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

function ProgressBadge({ status }) {
    if (!status) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    const s = String(status).toUpperCase();
    const ok = s.includes('COMPLETE') || s.includes('SUCCESS');
    const processing = s.includes('PROCESS') || s.includes('PROGRESS');
    const p = ok
        ? { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e' }
        : processing
        ? { bg: '#fffbeb', fg: '#b45309', border: '#fde68a', dot: '#d97706' }
        : { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca', dot: '#ef4444' };
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            px: '9px', py: '4px', borderRadius: '20px',
            fontSize: '11px', fontWeight: 700,
            bgcolor: p.bg, color: p.fg, border: `1px solid ${p.border}`,
        }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.dot, flexShrink: 0 }} />
            {status}
        </Box>
    );
}

const getStatusText = (status) => {
    switch (status) {
        case "NPC": return "No price change";
        case "S":   return "Success";
        case "NWC": return "No weight change";
        case "CLS": return "Already Closed";
        case "ERR": return "Error";
        case "ONF": return "Order Not Found";
        case "NEX": return "Excess ≤ ₹0 · not raised";
        default:    return status || "No Status";
    }
};

const extractDataForModal = (rowData) => {
    if (!rowData) return [];
    return Object.keys(rowData).map((key) => {
        const data = rowData[key];
        return {
            awb: data?.awb ?? null, op: data?.op ?? null, np: data?.np ?? null,
            ow: data?.ow ?? null,   nw: data?.nw ?? null, oz: data?.oz ?? null,
            nz: data?.nz ?? null,   status: data?.s ?? null, em: data?.em ?? null,
        };
    });
};

// ─── Component ────────────────────────────────────────────────────────────────

const UploadedMISFiles = () => {
    const [dataa, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page1, setPage1] = useState(0);
    const [sizePerPage1, setSizePerPage1] = useState(20);
    const [isReloading, setIsReloading] = useState(false);
    const [seeOrders, setSeeOrders] = useState(false);
    const [seeSummary, setSeeSummary] = useState(false);
    const [orderData, setOrderData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const methods = useForm({ defaultValues: { awb_number: '' } });
    const { handleSubmit, reset } = methods;

    useEffect(() => {
        setIsReloading(true);
        getGeneric(`${apiConstants.MIS_PROCESS_STATUS}?days=15&fileUUID&excludes`).then((res) => {
            setData(res?.data?.responseObj);
            _.forEach(res?.data?.responseObj, function (value) {
                if (value.fileId === '6635c212b42ee637c27b2775') {
                    let awbWiseStatus = value?.awbWiseStatus;
                    console.log(awbWiseStatus);
                }
            });
            setIsReloading(false);
        }).catch((err) => { setIsReloading(false); console.log(err); });
    }, []);

    const onSubmit = async (filters) => {
        let k = _.filter(orderData, (o) => o.awb === filters.awb_number);
        setFilteredData(k);
    };

    const downloadReport = (data) => {
        getGeneric(`${apiConstants.MIS_PROCESS_DOWNLOAD}?fileUUID=${data.uuid}`).then((res) => {
            downloadFile(res.data, "WD report.csv");
        }).catch((err) => { setIsReloading(false); console.log(err); });
    };

    const columnsOrderModal = [
        {
            field: "awb", headerAlign: 'left', hideSortIcons: true, headerName: "AWB", flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {row?.awb}
                    <CopyIcon text={row?.awb} />
                </Box>
            ),
        },
        { field: "op", headerAlign: 'left', hideSortIcons: true, headerName: "Original Price", flex: 0.7, renderCell: ({ row }) => row?.op },
        { field: "np", headerAlign: 'left', hideSortIcons: true, headerName: "New Price", flex: 0.7, renderCell: ({ row }) => row?.np },
        { field: "ow", headerAlign: 'left', hideSortIcons: true, headerName: "Orig Weight (gms)", flex: 0.8, renderCell: ({ row }) => formatNumber(row?.ow) },
        { field: "nw", headerAlign: 'left', hideSortIcons: true, headerName: "New Weight (gms)", flex: 0.8, renderCell: ({ row }) => formatNumber(row?.nw) },
        { field: "oz", headerAlign: 'left', hideSortIcons: true, headerName: "Orig Zone", flex: 0.7, renderCell: ({ row }) => row?.oz || '—' },
        { field: "nz", headerAlign: 'left', hideSortIcons: true, headerName: "New Zone", flex: 0.7, renderCell: ({ row }) => row?.nz || '—' },
        {
            field: "status", headerAlign: 'left', hideSortIcons: true, headerName: "Status", flex: 0.8,
            renderCell: ({ row }) => <ProgressBadge status={getStatusText(row.status)} />,
        },
        { field: "em", headerAlign: 'left', hideSortIcons: true, headerName: "Error Message", flex: 1, renderCell: ({ row }) => row?.em || '—' },
    ];

    const statusModal = [
        {
            field: "status", headerAlign: 'left', hideSortIcons: true, headerName: "WD Status", flex: 1,
            renderCell: ({ row }) => row.status === 'Total'
                ? <CellLine bold>{row.status}</CellLine>
                : <ProgressBadge status={getStatusText(row.status)} />,
        },
        { field: "count", headerAlign: 'left', hideSortIcons: true, headerName: "AWB Count", flex: 1, renderCell: ({ row }) => <CellLine bold>{row.count}</CellLine> },
    ];

    const columns = [
        {
            field: 'id',
            headerName: 'File ID / Remark', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold ellipsis>{row?.id}</CellLine>
                    {row?.remark && <CellLine muted ellipsis>{row.remark}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'createdBy',
            headerName: 'Uploaded By / At', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 240, flex: 1.4,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row?.createdBy || '—'}</CellLine>
                    <CellLine muted>{row?.createdAt ? formatDate(row.createdAt) : '—'}</CellLine>
                </CellStack>
            ),
        },
        {
            field: 'file',
            headerName: 'File', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => {
                if (!row?.s3Link?.signedLink) return <CellLine muted>—</CellLine>;
                return (
                    <Box component="a" href={row.s3Link.signedLink} target="_blank" rel="noreferrer"
                        sx={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd', '&:hover': { borderBottomStyle: 'solid' } }}>
                        Download
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 160,
            renderCell: ({ row }) => <ProgressBadge status={row?.status} />,
        },
        {
            field: 'progress',
            headerName: 'Progress', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row?.processedEntries ?? 0}<Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>/{row?.totalOrders ?? 0}</Box>
                </Box>
            ),
        },
        {
            field: 'successWD',
            headerName: 'Accepted', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => {
                const orders = extractDataForModal(row?.awbWiseStatus);
                const n = _.filter(orders, { 'status': 'S' }).length;
                return <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#15803d' }}>{n}</Box>;
            },
        },
        {
            field: 'failedWD',
            headerName: 'Rejected', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => {
                const orders = extractDataForModal(row?.awbWiseStatus);
                const success = _.filter(orders, { 'status': 'S' }).length;
                const n = orders.length - success;
                return <Box sx={{ fontSize: '13px', fontWeight: 700, color: n > 0 ? '#dc2626' : '#6b7280' }}>{n}</Box>;
            },
        },
        {
            field: 'processingStatus',
            headerName: '', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => {
                const actions = [
                    { key: 'download', label: 'Download report', icon: <DownloadSharpIcon fontSize="small" />, onClick: () => downloadReport(row) },
                    ...(row?.awbWiseStatus ? [
                        {
                            key: 'details', label: 'View AWB details', icon: <VisibilityIcon fontSize="small" />,
                            onClick: () => {
                                const d = extractDataForModal(row?.awbWiseStatus);
                                setOrderData(d); setFilteredData(d); setSeeOrders(true);
                            },
                            dividerBefore: true,
                        },
                        {
                            key: 'summary', label: 'View summary', icon: <Summarize fontSize="small" />,
                            onClick: () => {
                                const d = extractDataForModal(row?.awbWiseStatus);
                                const counts = _.countBy(d, 'status');
                                let total = 0;
                                const rows = Object.keys(counts).map((key, i) => { total += counts[key]; return { id: i, status: key, count: counts[key] }; });
                                rows.push({ id: rows.length, status: 'Total', count: total });
                                setOrderData(rows); setSeeSummary(true);
                            },
                        },
                    ] : []),
                ];
                return <OrderRowActions actions={actions} />;
            },
        },
    ];

    return (
        <>
            {isReloading && <Loader />}

            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    rows={dataa.length > 0 ? dataa : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    paginationMode={'client'}
                    getRowHeight={() => 'auto'}
                    rowCount={dataa?.length}
                    getRowId={(row) => row.id}
                />
            </Box>

            {/* ── AWB Details modal ─────────────────────────────────────── */}
            <MUIModal open={seeOrders} title="AWB Details" maxWidth={"xl"} handleClose={() => setSeeOrders(false)}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end', mb: 2 }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb_number" label="" placeholder="Filter by AWB" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button type="submit" variant="contained" size="small" disableElevation
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Button>
                                <Button type="button" variant="contained" size="small" disableElevation
                                    onClick={() => { reset({ merchant: '' }); setFilteredData(orderData); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', px: 2.5, py: 1, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Button>
                            </Box>
                        </Box>
                    </FormProvider>
                </Box>
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <ProzoDataGrid
                        sizePerPage={sizePerPage1} setSizePerPage={setSizePerPage1}
                        page={page1} setPage={setPage1}
                        rowCount={orderData?.length}
                        columns={columnsOrderModal}
                        rows={filteredData}
                        paginationMode={'client'}
                        pagination={true}
                        hideFooterPagination={false}
                        getRowHeight={() => 'auto'}
                        getRowId={() => Math.random()}
                    />
                </Box>
            </MUIModal>

            {/* ── Summary modal ─────────────────────────────────────────── */}
            <MUIModal open={seeSummary} title="WD Upload Summary" maxWidth={"sm"} handleClose={() => setSeeSummary(false)}>
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', mt: 1 }}>
                    <ProzoDataGrid
                        sizePerPage={sizePerPage1} setSizePerPage={setSizePerPage1}
                        page={page1} setPage={setPage1}
                        rowCount={orderData?.length}
                        columns={statusModal}
                        rows={orderData}
                        paginationMode={'client'}
                        pagination={true}
                        hideFooterPagination={false}
                        getRowId={() => Math.random()}
                    />
                </Box>
            </MUIModal>
        </>
    );
};

export default UploadedMISFiles;
